import React, { useRef, useEffect, useState } from "react";
import { Users, Moon, Smartphone } from "lucide-react";
import { useTranslation } from "react-i18next";

function useReveal(threshold = 0.15) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);
  return [ref, visible];
}

const BenefitsSection = () => {
  const { t } = useTranslation('home');
  const [sectionRef, sectionVisible] = useReveal(0.1);

  const benefits = [
    {
      icon: Users,
      title: t('benefits_0_title'),
      subtitle: t('benefits_0_subtitle'),
      accent: "#a346d5",
      iconBg: "rgba(163,70,213,0.10)",
      tag: t('benefits_0_tag'),
    },
    {
      icon: Moon,
      title: t('benefits_1_title'),
      subtitle: t('benefits_1_subtitle'),
      accent: "#009efa",
      iconBg: "rgba(0,158,250,0.12)",
      tag: t('benefits_1_tag'),
    },
    {
      icon: Smartphone,
      title: t('benefits_2_title'),
      subtitle: t('benefits_2_subtitle'),
      accent: "#4caf89",
      iconBg: "rgba(76,175,137,0.12)",
      tag: t('benefits_2_tag'),
    },
  ];

  return (
    <section
      style={{
        padding: "52px 0 20px",
        background: "#f7f7f8",
        position: "relative",
      }}
    >
      <div
        ref={sectionRef}
        style={{ maxWidth: "920px", margin: "0 auto", padding: "0 24px" }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center",
          gap: "12px", marginBottom: "32px",
          opacity: sectionVisible ? 1 : 0,
          transform: sectionVisible ? "translateY(0)" : "translateY(20px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}>
          <div style={{
            height: "1px", flex: "1", maxWidth: "60px",
            background: "linear-gradient(90deg, transparent, rgba(163,70,213,0.30))",
          }} />
          <h2 style={{
            fontSize: "clamp(18px, 3vw, 24px)", fontWeight: 700,
            color: "#0f0f14", lineHeight: 1.3, letterSpacing: "-0.01em",
            margin: 0, textAlign: "center",
          }}>
            {t('benefits_heading')}
          </h2>
          <div style={{
            height: "1px", flex: "1", maxWidth: "60px",
            background: "linear-gradient(270deg, transparent, rgba(163,70,213,0.30))",
          }} />
        </div>

        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "14px",
        }}>
          {benefits.map((b, idx) => {
            const Icon = b.icon;
            return (
              <div
                key={idx}
                style={{
                  background: "#ffffff",
                  border: "1px solid #ebebef",
                  borderRadius: "18px",
                  padding: "22px 20px 20px",
                  display: "flex", flexDirection: "column", gap: "0",
                  boxShadow: "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)",
                  transition: `opacity 0.5s ease ${idx * 0.08}s, transform 0.5s ease ${idx * 0.08}s, box-shadow 0.25s cubic-bezier(0.22, 1, 0.36, 1)`,
                  opacity: sectionVisible ? 1 : 0,
                  transform: sectionVisible ? "translateY(0)" : "translateY(24px)",
                  cursor: "default",
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.boxShadow = "0 4px 8px rgba(13,22,25,0.06), 0 16px 40px rgba(13,22,25,0.1)";
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.boxShadow = "0 1px 3px rgba(13,22,25,0.04), 0 12px 32px rgba(13,22,25,0.06)";
                }}
              >
                <div style={{
                  display: "flex", alignItems: "center", gap: "12px",
                  marginBottom: "12px",
                }}>
                  <div style={{
                    width: "38px", height: "38px", borderRadius: "10px",
                    background: b.iconBg,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <Icon style={{ width: "18px", height: "18px", color: b.accent, strokeWidth: 2.2 }} />
                  </div>
                  <span style={{
                    fontSize: "10px", fontWeight: 600, letterSpacing: "0.08em",
                    textTransform: "uppercase", color: b.accent,
                  }}>
                    {b.tag}
                  </span>
                </div>

                <h3 style={{
                  fontWeight: 700, fontSize: "15px",
                  color: "#0f0f14", lineHeight: 1.35, margin: "0 0 6px",
                }}>
                  {b.title}
                </h3>
                <p style={{
                  fontSize: "13px", fontWeight: 400,
                  color: "#5b5b6b", margin: 0, lineHeight: 1.5,
                }}>
                  {b.subtitle}
                </p>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 700px) {
          section > div > div:last-child {
            grid-template-columns: 1fr !important;
            gap: 10px !important;
          }
        }
      `}</style>
    </section>
  );
};

export default BenefitsSection;